import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { AxiosError } from "axios";

import { Dialog } from "components/common/dialog/Dialog";
import { SliderPageWrapper } from "components/layout/sliderPageWrapper/SliderPageWrapper";

import { extractError, renderConditionalProp } from "utility";

import { useRootDispatch, useRootSelector } from "store/hooks";
import { useRequestDispatch } from "store/requestHooks";

import {
  currentAutoPay,
  selectedAccountInfo,
} from "store/account/account-selector";

import { FetchAutoPay, fetchAutoPay } from "store/payments/payments-action";
import {
  DeletePaymentMethods,
  deletePaymentMethods,
} from "store/paymentMethods/paymentMethods-action";

import { updateSlide } from "store/common/common-slice";
import { AutoPayDTO } from "store/payments/payments-state";
import { selectAllschedulePayments } from "store/schedulePayments/schedulePayments-slice";
import { useAddNotification } from "hooks";

import {
  SchedulePayments,
  ScheduledPaymentsFilter,
} from "store/schedulePayments/schedulePayments-state";
import {
  FetchSchedulePayments,
  fetchSchedulePayments,
} from "store/schedulePayments/schedulePayments-action";
import classes from "./DeleteConfirmation.module.scss";
import { selectIsPaymentMethodUsedInAnyPayByText } from "store/paybytext/paybytext-selectors";

export interface CheckListMethodDelete {
  isPayByTextLinked: boolean;
  isAutoPayLinked: boolean;
  isScheduleLinked: boolean;
  autoPayData: AutoPayDTO | null;
  schedulePayments: null | SchedulePayments[];
  paymentMethodId: number | null;
}

export const DeleteConfirmation = () => {
  const { t } = useTranslation(["common", "payment"]);

  const dispatch = useRootDispatch();
  const requestDispatch = useRequestDispatch();
  const addNotification = useAddNotification();

  const { pageDetail } = useRootSelector((state) => state.common);
  const { meta } = useRootSelector((state) => state.paymentMethods);
  const schedulePayments = useRootSelector(selectAllschedulePayments);
  const accountInfo = useRootSelector(selectedAccountInfo);
  const autoPayInfo = useRootSelector(currentAutoPay);

  const payByTextData = useRootSelector(
    useMemo(
      () => selectIsPaymentMethodUsedInAnyPayByText(pageDetail?.data?.id),
      [pageDetail],
    ),
  );

  const [openDialog, setOpenDialog] = useState(false);

  const deleteHandler = () => {
    let paymentMethodId = pageDetail?.data?.id;
    if (paymentMethodId) {
      const actionCheckList: CheckListMethodDelete = {
        isPayByTextLinked: payByTextData,
        isAutoPayLinked: false,
        isScheduleLinked: false,
        autoPayData: null,
        schedulePayments: null,
        paymentMethodId,
      };
      if (autoPayInfo) {
        let isMethodUsed =
          autoPayInfo.paymentMethodId === paymentMethodId &&
          autoPayInfo.status !== 2;
        if (isMethodUsed) {
          actionCheckList.isAutoPayLinked = true;
          actionCheckList.autoPayData = autoPayInfo;
        }
      }
      if (schedulePayments && schedulePayments.length > 0) {
        const schedulePaymentsObj = schedulePayments.filter(
          (single) => single.paymentMethodId === paymentMethodId,
        );
        if (schedulePaymentsObj && schedulePaymentsObj.length > 0) {
          actionCheckList.isScheduleLinked = true;
          actionCheckList.schedulePayments = schedulePaymentsObj;
        }
      }
      if (
        actionCheckList.isAutoPayLinked ||
        actionCheckList.isScheduleLinked ||
        actionCheckList.isPayByTextLinked
      ) {
        addNotification({
          messages: ["payment_method_delete_err"],
          type: "ERROR",
          closable: true,
        });
      } else {
        deletePayment(paymentMethodId);
      }
    } else {
      console.log("Some thing went Wrong, No ID found.");
    }
  };

  const deletePayment = async (paymentMethodId: number) => {
    requestDispatch<DeletePaymentMethods, number>(
      deletePaymentMethods,
      paymentMethodId,
    )
      .unwrap()
      .then(() => {
        setOpenDialog(true);
        requestDispatch<FetchAutoPay>(fetchAutoPay);
        requestDispatch<FetchSchedulePayments, ScheduledPaymentsFilter>(
          fetchSchedulePayments,
          {
            accountInformationId: accountInfo?.id!,
            status: [0, 1],
          },
        );
      })
      .catch((err: AxiosError) => {
        let errCode = extractError(err);
        if (err.message.includes("409")) {
          errCode = ["payment_method_delete_err"];
        }
        addNotification({
          messages: errCode,
          type: "ERROR",
          closable: true,
        });
      });
  };

  return (
    <SliderPageWrapper headerTitle={t("payment:delete_payment_method")}>
      {meta?.isLoading ? <ft-screen-loader></ft-screen-loader> : null}
      <div className={classes.Wrapper}>
        <p>{t("payment:delete_payment_confirm")}</p>
        <p>{t("payment:delete_payment_information")}</p>
        <div className={classes.Buttons}>
          <ft-button
            class={[classes.Back, "outline-button"].join(" ")}
            label={t("common:back_label")}
            outlined
            onClick={() => {
              dispatch(updateSlide({ id: "ManageWallet" }));
            }}
          ></ft-button>
          <ft-button
            class={classes.Delete}
            label={t("common:delete")}
            unelevated
            {...renderConditionalProp(meta.isLoading, "disabled")}
            onClick={deleteHandler}
          ></ft-button>
        </div>
        {openDialog ? (
          <Dialog
            icon="delete_forever"
            dialogClosed={() => {
              setOpenDialog(false);
              dispatch(updateSlide({ id: "ManageWallet" }));
            }}
            type="default"
            hideActions={true}
            closeIcon={true}
          >
            {t("payment:delete_payment_successfully")}
          </Dialog>
        ) : null}
      </div>
    </SliderPageWrapper>
  );
};
